import { maxBy, minBy } from 'lodash';
const rowTypes = {
  SINGLE_IN_GROUP: 'single-in-group',
  GROUPED: 'grouped',
  GROUP_HEADER: 'group-header'
};
const createPickupData = (pickup, rowType) => ({
  ...pickup,
  id: pickup.pickupId,
  groupId: pickup.groupId?.toUpperCase(),
  pickupNumberIdList: [
    {
      pickupId: pickup.pickupId,
      pickupNumber: pickup.pickupNumber,
      isPrimary: pickup.isPrimary
    }
  ],
  pickupIdList: [pickup.pickupId],
  pickupNumberList: [pickup.pickupNumber],
  rowType: rowType
});

const getPickupNumberIdListFromPickup = (pickup) => {
  if (!pickup) {
    return [];
  }

  if (Array.isArray(pickup.pickupNumberIdList) && pickup.pickupNumberIdList.length) {
    return pickup.pickupNumberIdList;
  }

  if (!pickup.pickupId && !pickup.pickupNumber) {
    return [];
  }

  return [
    {
      pickupId: pickup.pickupId,
      pickupNumber: pickup.pickupNumber,
      isPrimary: pickup.isPrimary
    }
  ];
};

// Define boolean fields once at the top of the function or module
const BOOLEAN_FIELDS = [
  'isSwapPickup',
  'isDailyPickup',
  'is3PlPickup',
  'isDockDropPickup',
  'isExhibitPickup',
  'isApt',
  'isFood',
  'isPoison',
  'isHazmat',
  'isFreezable',
  'isInbond',
  'isResidential',
  'isLiftGate',
  'isLimitedAccess',
  'isExcessiveLength',
  'isResunaDeliveryStop',
  'isCodShipment',
  'isLiftgateReqd',
  'isTwoManRequired',
  'isCargoVanEligible',
  'maxEquipmentSize',
  'minEquipmentSize',
  'fixedEquipmentSize',
  'isVoided',
  'isCompleted',
  'isMissed',
  'isAcknowledged'
];

// Optimized aggregation function - only essential fields
const aggregateGroupValues = (groupPickups) => {
  const addToSet = (set, value) => {
    if (Array.isArray(value)) {
      value.forEach(item => set.add(item));
    } else if (value != null) {
      set.add(value);
    }
  };
  const isNoFreight = () => {
    return groupPickups.every((pickup) => pickup.isNoFreight === true);
  };

  const concatWithNewline = (existing, newValue) =>
    existing ? `${existing}\n${newValue}` : newValue;

  // Initialize accumulator with boolean fields
  const initialAcc = {
    bills: 0,
    pieces: 0,
    handlingUnits: 0,
    weight: 0,
    packageTypes: new Set(),
    quoteNumbers: new Set(),
    pickupTypes: new Set(),
    priority: new Set(),
    specialInstructions: '',
    shipperComments: '',
    isNoFreight: false,
    createdDateTime: null,
    pse: 0,
    ...Object.fromEntries(BOOLEAN_FIELDS.map(field => [field, false]))
  };

  const aggregated = groupPickups.reduce(
    (acc, pickup) => {
      // Sum numeric values
      acc.bills += pickup.bills || 0;
      acc.pieces += pickup.pieces || 0;
      acc.handlingUnits += pickup.hu || 0;
      acc.weight += pickup.weight || 0;
      acc.pse += pickup.pse || 0;

      // Add to sets
      addToSet(acc.packageTypes, pickup.packageType);
      addToSet(acc.pickupTypes, pickup.pickupType);
      addToSet(acc.priority, pickup.priority);

      if (pickup.quoteNumber) acc.quoteNumbers.add(pickup.quoteNumber);

      // Concatenate strings
      if (pickup.specialInstructions) {
        acc.specialInstructions = concatWithNewline(
          acc.specialInstructions,
          pickup.specialInstructions
        );
      }
      if (pickup.shipperComments) {
        acc.shipperComments = concatWithNewline(acc.shipperComments, pickup.shipperComments);
      }
      if (
        !acc.createdDateTime ||
        new Date(pickup.createdDateTime) < new Date(acc.createdDateTime)
      ) {
        acc.createdDateTime = pickup.createdDateTime;
      }

      // Boolean OR operations - using loop instead of repetitive assignments
      BOOLEAN_FIELDS.forEach(field => {
        acc[field] ||= !!pickup[field];
      });

      return acc;
    },
    initialAcc
  );

  // Build return object dynamically
  return {
    bills: aggregated.bills,
    pieces: aggregated.pieces,
    handlingUnits: aggregated.handlingUnits,
    weight: aggregated.weight,
    packageTypes: Array.from(aggregated.packageTypes),
    quoteNumbers: Array.from(aggregated.quoteNumbers),
    pickupTypes: Array.from(aggregated.pickupTypes),
    priority: Array.from(aggregated.priority).length ? Array.from(aggregated.priority) : null,
    specialInstructions: aggregated.specialInstructions || null,
    shipperComments: aggregated.shipperComments || null,
    ...Object.fromEntries(BOOLEAN_FIELDS.map(field => [field, aggregated[field]])),
    isNoFreight: isNoFreight(),
    pse: aggregated.pse,
    createdDateTime: aggregated.createdDateTime
  };
};

// Create group header
const createGroupHeader = (groupId, groupPickups, primaryPickup, aggregatedValues) => ({
  ...primaryPickup,
  id: groupId,
  groupId: groupId.toUpperCase(),
  rowType: rowTypes.GROUP_HEADER,
  pickupId: groupPickups.map((p) => p.pickupId).join(', '),
  pickupNumber: '',
  pickupNumberIdList: groupPickups.map((p) => {
    return {
      pickupId: p.pickupId,
      pickupNumber: p.pickupNumber,
      isPrimary: p.isPrimary
    };
  }),
  pickupIdList: groupPickups.map((p) => p.pickupId),
  pickupNumberList: groupPickups.map((p) => p.pickupNumber),
  bills: aggregatedValues.bills,
  pieces: aggregatedValues.pieces,
  hu: aggregatedValues.handlingUnits,
  weight: aggregatedValues.weight,
  packageType: aggregatedValues.packageTypes,
  priority: aggregatedValues.priority,
  allQuoteNumbers: aggregatedValues.quoteNumbers.join(', '),
  specialInstructions: aggregatedValues.specialInstructions,
  allPickupTypes: aggregatedValues.pickupTypes,
  groupCount: groupPickups.length,
  shipperComments: aggregatedValues.shipperComments,
  ...Object.fromEntries(BOOLEAN_FIELDS.filter(field => 
    !['isVoided', 'isCompleted', 'isMissed', 'isAcknowledged'].includes(field)
  ).map(field => [field, aggregatedValues[field]])),
  pse: aggregatedValues.pse,
  pickupCloseTime: maxBy(groupPickups, 'pickupCloseTime')?.pickupCloseTime ?? null,
  pickupReadyTime: minBy(groupPickups, 'pickupReadyTime')?.pickupReadyTime ?? null,
  groupAggregatedData: {
    isVoided: aggregatedValues.isVoided,
    isNoFreight: aggregatedValues.isNoFreight,
    isCompleted: aggregatedValues.isCompleted,
    isMissed: aggregatedValues.isMissed,
    isAcknowledged: aggregatedValues.isAcknowledged,
    pickupDate: primaryPickup.pickupDate,
    driverId: primaryPickup.driverId,
    createdDateTime: aggregatedValues.createdDateTime
  }
});

// Process a single group
const processGroup = (groupId, groupPickups) => {
  if (groupPickups.length === 1) {
    return [createPickupData(groupPickups[0], rowTypes.SINGLE_IN_GROUP)];
  }

  const primaryPickup = groupPickups.find((p) => p.isPrimary) || groupPickups[0];
  const aggregatedValues = aggregateGroupValues(groupPickups);

  const groupHeader = createGroupHeader(groupId, groupPickups, primaryPickup, aggregatedValues);

  const groupedPickups = groupPickups.map((pickup) => createPickupData(pickup, rowTypes.GROUPED));

  return [groupHeader, ...groupedPickups];
};

const createGroupedData = (pickupsList) => {
  if (!pickupsList?.length) return [];

  // Filter pickups that need processing
  const processPickups = pickupsList.filter((pickup) => !pickup.rowType || !isGroupHeaders(pickup));

  // Group pickups by groupId using reduce
  const groupedByGroupId = processPickups.reduce((groups, pickup) => {
    const groupId = pickup.groupId?.toUpperCase();
    if (!groupId) {
      return groups;
    }
    groups[groupId] = groups[groupId] || [];
    groups[groupId].push(pickup);
    return groups;
  }, {});

  const groupItems = Object.entries(groupedByGroupId)
    .map(([groupId, groupPickups]) => processGroup(groupId, groupPickups))
    .flat();

  return groupItems;
};

// ---------------------------------------------------------------------------
// Reference-stable memoization for createDateOrderedGroupedData.
//
// A single pickup changing (the common case for a live SignalR update) should
// not force every other row and group header in the terminal to be rebuilt
// from scratch. These module-scoped caches hold the *previous call's* results;
// each call builds a fresh "next" generation and swaps it in at the end
// (generation cutover) — self-cleaning, since anything not seen in the current
// call (a completed/voided/moved pickup) is simply not carried forward, so the
// caches never grow unbounded.
//
// Reuse is keyed on object *reference* equality of the underlying pickups —
// upstream store code already gives an unchanged pickup the same object
// reference across updates (only pickups actually named in an event get a new
// `{ ...old, ...update }` object), so this only has to stop *this* file from
// throwing that stability away.
//
// Sort is not memoized: each call re-sorts groups by pickupDate desc (same as
// release/5.4 / createDateOrderedGroupedDataUnmemoized). Equal timestamps keep
// Object.keys / pickupsList encounter order (stable sort), so prepended
// same-day arrivals stay on top.
//
// Scoped deliberately to createDateOrderedGroupedData (the grid's data
// source via DispatchStore.groupedPickupList). createGroupedData / processGroup
// above are untouched and keep their exact existing behavior for their other
// callers (e.g. the possible-merge filter).
// ---------------------------------------------------------------------------
let previousPickupRowCache = new Map(); // upperPickupId -> { source, rowType, row }
let previousGroupCache = new Map(); // upperGroupId -> { memberRefs, sortTimestamp, groupRows }

const getMemoizedPickupRow = (pickup, rowType, nextRowCache) => {
  const key = pickup?.pickupId?.toUpperCase();
  const cached = key ? previousPickupRowCache.get(key) : undefined;
  if (cached && cached.source === pickup && cached.rowType === rowType) {
    if (key) nextRowCache.set(key, cached);
    return cached.row;
  }
  const row = createPickupData(pickup, rowType);
  if (key) nextRowCache.set(key, { source: pickup, rowType, row });
  return row;
};

// Same pickup references, same order — the only way any field feeding this
// group's header or rows could have changed. Order matters: specialInstructions
// / shipperComments concatenation and the pickupNumberIdList / pickupIdList /
// pickupNumberList arrays are order-sensitive and user-visible.
const isSameGroupMembership = (previousMemberRefs, currentMemberPickups) => {
  if (!previousMemberRefs || previousMemberRefs.length !== currentMemberPickups.length) {
    return false;
  }
  return previousMemberRefs.every((pickup, index) => pickup === currentMemberPickups[index]);
};

const buildGroupSection = (groupId, groupPickups, nextRowCache, nextGroupCache) => {
  const previousSection = previousGroupCache.get(groupId);
  if (previousSection && isSameGroupMembership(previousSection.memberRefs, groupPickups)) {
    // Nothing about this group changed — carry its cached rows/header and its
    // members' row-cache entries forward into the next generation as-is.
    groupPickups.forEach((pickup) => {
      const key = pickup.pickupId?.toUpperCase();
      const cachedRow = key && previousPickupRowCache.get(key);
      if (cachedRow) nextRowCache.set(key, cachedRow);
    });
    nextGroupCache.set(groupId, previousSection);
    return previousSection;
  }

  const groupRows =
    groupPickups.length === 1
      ? [getMemoizedPickupRow(groupPickups[0], rowTypes.SINGLE_IN_GROUP, nextRowCache)]
      : (() => {
          const primaryPickup = groupPickups.find((p) => p.isPrimary) || groupPickups[0];
          const aggregatedValues = aggregateGroupValues(groupPickups);
          const groupHeader = createGroupHeader(groupId, groupPickups, primaryPickup, aggregatedValues);
          const groupedPickups = groupPickups.map((pickup) =>
            getMemoizedPickupRow(pickup, rowTypes.GROUPED, nextRowCache)
          );
          return [groupHeader, ...groupedPickups];
        })();

  const topLevelRow = groupRows[0];
  const section = {
    memberRefs: groupPickups.slice(),
    sortTimestamp: new Date(topLevelRow?.pickupDate || topLevelRow?.createdAt || 0).getTime(),
    groupRows
  };
  nextGroupCache.set(groupId, section);
  return section;
};

/**
 * Like createGroupedData, but top-level rows ordered by pickupDate (desc).
 * Children stay under their header. Used by DispatchStore.groupedPickupList.
 *
 * Reference-memoized for row/header objects only. Group order is a full
 * re-sort each call (O(G log G)), matching release/5.4.
 */
const createDateOrderedGroupedData = (pickupsList = []) => {
  if (!pickupsList?.length) {
    previousPickupRowCache = new Map();
    previousGroupCache = new Map();
    return [];
  }

  const processPickups = pickupsList.filter(
    (pickup) => pickup?.pickupId && (!pickup.rowType || !isGroupHeaders(pickup))
  );

  const groupedByGroupId = processPickups.reduce((groups, pickup) => {
    const groupId = pickup.groupId?.toUpperCase();
    if (!groupId) {
      return groups;
    }
    groups[groupId] = groups[groupId] || [];
    groups[groupId].push(pickup);
    return groups;
  }, {});

  const nextRowCache = new Map();
  const nextGroupCache = new Map();

  Object.entries(groupedByGroupId).forEach(([groupId, groupPickups]) => {
    buildGroupSection(groupId, groupPickups, nextRowCache, nextGroupCache);
  });

  // Same as 5.4: sort by pickupDate desc; equal dates keep Object.keys /
  // pickupsList first-seen order (stable sort → prepended same-day on top).
  const sortedGroupIds = Object.keys(groupedByGroupId).sort(
    (leftGroupId, rightGroupId) =>
      nextGroupCache.get(rightGroupId).sortTimestamp -
      nextGroupCache.get(leftGroupId).sortTimestamp
  );

  previousPickupRowCache = nextRowCache;
  previousGroupCache = nextGroupCache;

  return sortedGroupIds.flatMap((groupId) => nextGroupCache.get(groupId).groupRows);
};

/**
 * Always-fresh reference implementation of createDateOrderedGroupedData — same
 * output, no caching, full re-sort every call. Kept only so tests can assert
 * the memoized version above produces an identical result (values, order) to
 * a guaranteed-correct-by-construction rebuild-from-scratch, across arbitrary
 * sequences of adds/updates/removes/group-membership changes.
 */
const createDateOrderedGroupedDataUnmemoized = (pickupsList = []) => {
  if (!pickupsList?.length) return [];

  const processPickups = pickupsList.filter(
    (pickup) => pickup?.pickupId && (!pickup.rowType || !isGroupHeaders(pickup))
  );

  const groupedByGroupId = processPickups.reduce((groups, pickup) => {
    const groupId = pickup.groupId?.toUpperCase();
    if (!groupId) {
      return groups;
    }
    groups[groupId] = groups[groupId] || [];
    groups[groupId].push(pickup);
    return groups;
  }, {});

  return Object.entries(groupedByGroupId)
    .map(([groupId, groupPickups]) => {
      const groupRows = processGroup(groupId, groupPickups);
      const topLevelRow = groupRows[0];
      return {
        sortTimestamp: new Date(topLevelRow?.pickupDate || topLevelRow?.createdAt || 0).getTime(),
        groupRows
      };
    })
    .sort((left, right) => right.sortTimestamp - left.sortTimestamp)
    .flatMap((section) => section.groupRows);
};

// Optimized search function for quick filter text
const searchInData = (data, searchText) => {
  if (!data || !searchText) return false;

  const lowerSearchText = searchText.toLowerCase();

  const searchRecursive = (obj) => {
    if (obj === null || obj === undefined) return false;

    // Handle primitives
    if (typeof obj === 'string' || typeof obj === 'number') {
      return obj.toString().toLowerCase().includes(lowerSearchText);
    }

    // Handle arrays - search with early exit using some()
    if (Array.isArray(obj)) {
      return obj.some((item) => searchRecursive(item));
    }

    // Handle objects - search values with early exit using some()
    if (typeof obj === 'object') {
      return Object.values(obj).some((value) => searchRecursive(value));
    }

    return false;
  };

  return searchRecursive(data);
};

const getPickupsInGroup = (groupId, allPickupsList) => {
  const normalizedGroupId = groupId?.toUpperCase();
  if (!normalizedGroupId) return [];
  return (allPickupsList || []).filter(
    (pickup) =>
      pickup.groupId?.toUpperCase() === normalizedGroupId && !isGroupHeaders(pickup)
  );
};

const isGroupHeaders = (row) => row.rowType === rowTypes.GROUP_HEADER;
const isGroupedPickups = (row) => row.rowType === rowTypes.GROUPED;
const isSingleInGroupPickups = (row) => row.rowType === rowTypes.SINGLE_IN_GROUP;
const isPickupGroupHeader = (row) => isGroupHeaders(row) || isSingleInGroupPickups(row);

export {
  createGroupedData,
  searchInData,
  isGroupHeaders,
  isGroupedPickups,
  isSingleInGroupPickups,
  isPickupGroupHeader,
  createGroupHeader,
  createPickupData,
  aggregateGroupValues,
  getPickupsInGroup,
  getPickupNumberIdListFromPickup,
  createDateOrderedGroupedData,
  createDateOrderedGroupedDataUnmemoized
};
